import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { BrowserRouter, Link, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import MockupStudio from "../../frame-designer/pro/MockupStudio";
import UploaderModal from "../../frame-designer/print/UploaderModal";
import {
  buildUploadsIndexUrl,
  getUploadsRouteType,
  normalizeRelativeUrl,
  removeQueryParam,
} from "./routeUtils";

const Icon = ({ className = "h-3.5 w-3.5", path }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 20 20"
    fill="currentColor"
    aria-hidden="true"
    className={className}
  >
    {path}
  </svg>
);

const ICONS = {
  magnifyingGlass: (
    <path
      fillRule="evenodd"
      d="M9 3.5a5.5 5.5 0 1 0 3.473 9.766l3.63 3.63a.75.75 0 1 0 1.06-1.06l-3.63-3.63A5.5 5.5 0 0 0 9 3.5ZM5 9a4 4 0 1 1 8 0 4 4 0 0 1-8 0Z"
      clipRule="evenodd"
    />
  ),
  sparkles: (
    <path
      fillRule="evenodd"
      d="M9 4.5a.75.75 0 0 1 .721.544l.813 2.846a3.75 3.75 0 0 0 2.576 2.576l2.846.813a.75.75 0 0 1 0 1.442l-2.846.813a3.75 3.75 0 0 0-2.576 2.576l-.813 2.846a.75.75 0 0 1-1.442 0l-.813-2.846a3.75 3.75 0 0 0-2.576-2.576l-2.846-.813a.75.75 0 0 1 0-1.442l2.846-.813A3.75 3.75 0 0 0 7.466 7.89l.813-2.846A.75.75 0 0 1 9 4.5Z"
      clipRule="evenodd"
    />
  ),
  photo: (
    <path
      fillRule="evenodd"
      d="M1.5 4.5A2.5 2.5 0 0 1 4 2h12a2.5 2.5 0 0 1 2.5 2.5v11A2.5 2.5 0 0 1 16 18H4a2.5 2.5 0 0 1-2.5-2.5v-11ZM4 3.5a1 1 0 0 0-1 1V12l3.012-2.51a1.75 1.75 0 0 1 2.246.038l.897.776 2.722-2.722a1.75 1.75 0 0 1 2.475 0L17 9.23V4.5a1 1 0 0 0-1-1H4Zm13 7.85-3.712-3.711a.25.25 0 0 0-.354 0l-2.73 2.73 3.468 3.002a.75.75 0 0 1-.982 1.132l-5.422-4.694a.25.25 0 0 0-.321-.005L3 13.17v2.33a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-4.15Z"
      clipRule="evenodd"
    />
  ),
  squareStack: (
    <path
      fillRule="evenodd"
      d="M8.25 2A2.25 2.25 0 0 0 6 4.25v7.5A2.25 2.25 0 0 0 8.25 14h7.5A2.25 2.25 0 0 0 18 11.75v-7.5A2.25 2.25 0 0 0 15.75 2h-7.5ZM7.5 4.25a.75.75 0 0 1 .75-.75h7.5a.75.75 0 0 1 .75.75v7.5a.75.75 0 0 1-.75.75h-7.5a.75.75 0 0 1-.75-.75v-7.5ZM2 8.25A2.25 2.25 0 0 1 4.25 6H5v1.5h-.75a.75.75 0 0 0-.75.75v7.5c0 .414.336.75.75.75h7.5a.75.75 0 0 0 .75-.75V15H14v.75A2.25 2.25 0 0 1 11.75 18h-7.5A2.25 2.25 0 0 1 2 15.75v-7.5Z"
      clipRule="evenodd"
    />
  ),
  document: (
    <path
      fillRule="evenodd"
      d="M4.25 2A2.25 2.25 0 0 0 2 4.25v11.5A2.25 2.25 0 0 0 4.25 18h11.5A2.25 2.25 0 0 0 18 15.75V7.06a2.25 2.25 0 0 0-.659-1.591l-2.81-2.81A2.25 2.25 0 0 0 12.94 2H4.25ZM13 3.81V6.5a.5.5 0 0 0 .5.5h2.69L13 3.81ZM3.5 4.25a.75.75 0 0 1 .75-.75h7.25v3A2 2 0 0 0 13.5 8.5h3v7.25a.75.75 0 0 1-.75.75H4.25a.75.75 0 0 1-.75-.75V4.25Z"
      clipRule="evenodd"
    />
  ),
  arrowTopRight: (
    <path
      fillRule="evenodd"
      d="M5.22 14.78a.75.75 0 0 0 1.06 0l7.22-7.22v5.69a.75.75 0 0 0 1.5 0v-7.5a.75.75 0 0 0-.75-.75h-7.5a.75.75 0 0 0 0 1.5h5.69l-7.22 7.22a.75.75 0 0 0 0 1.06Z"
      clipRule="evenodd"
    />
  ),
  check: (
    <path
      fillRule="evenodd"
      d="M16.704 5.29a1 1 0 0 1 0 1.415l-8 8a1 1 0 0 1-1.415 0l-4-4a1 1 0 1 1 1.415-1.414L8 12.585l7.29-7.295a1 1 0 0 1 1.414 0Z"
      clipRule="evenodd"
    />
  ),
  xMark: (
    <path
      fillRule="evenodd"
      d="M4.22 4.22a.75.75 0 0 1 1.06 0L10 8.94l4.72-4.72a.75.75 0 1 1 1.06 1.06L11.06 10l4.72 4.72a.75.75 0 1 1-1.06 1.06L10 11.06l-4.72 4.72a.75.75 0 0 1-1.06-1.06L8.94 10 4.22 5.28a.75.75 0 0 1 0-1.06Z"
      clipRule="evenodd"
    />
  ),
};

const PRODUCT_CARDS = [
  {
    id: "paper",
    title: "Print & frame",
    body: "Custom-cut frame with print on your chosen paper stock.",
    cta: "Configure",
    icon: ICONS.photo,
  },
  {
    id: "canvas",
    title: "Framed canvas",
    body: "Stretched canvas in a floater frame, gallery wrap.",
    cta: "Configure",
    icon: ICONS.squareStack,
  },
  {
    id: "printOnly",
    title: "Unframed print",
    body: "Loose archival print, ready to frame yourself.",
    cta: "Configure",
    icon: ICONS.document,
  },
];

function loadingCard(label = "Loading uploads…") {
  return (
    <div className="rounded-lg border border-[#E4E0D6] bg-white px-6 py-16 text-center text-[13px] text-[#6B6B66]">
      {label}
    </div>
  );
}

function errorCard(message, retry) {
  return (
    <div className="rounded-lg border border-[#E4D6D6] bg-[#FFF5F5] px-6 py-10">
      <h2 className="m-0 text-[15px] font-semibold text-[#7A1F1F]">
        Couldn&apos;t load this page
      </h2>
      <p className="mt-2 mb-0 text-[12px] leading-[18px] text-[#7A1F1F]">
        {message}
      </p>
      {retry ? (
        <button
          type="button"
          onClick={retry}
          className="mt-4 inline-flex items-center h-8 rounded-md px-3 bg-[#111111] text-white text-[12px] font-medium border-0 cursor-pointer"
        >
          Try again
        </button>
      ) : null}
    </div>
  );
}

function TablePagination({ pagination, onNavigate }) {
  if (!pagination || !pagination.items?.length) return null;

  const base =
    "inline-flex items-center justify-center h-7 min-w-7 rounded-md px-2 text-[12px] font-medium transition-colors no-underline border";
  const enabledBtn = `${base} bg-white border-[#E4E0D6] text-[#111111] hover:bg-[#FAF8F2] cursor-pointer`;
  const disabledBtn = `${base} bg-[#F6F4EE] border-[#F0EDE4] text-[#C9C5B8]`;
  const currentBtn = `${base} bg-[#111111] border-[#111111] text-white`;
  const gapBtn =
    "inline-flex items-center justify-center h-7 min-w-7 px-1 text-[12px] text-[#9A9890]";

  const renderNavButton = (label, url, disabled, direction) => {
    const path =
      direction === "previous" ? (
        <path
          fillRule="evenodd"
          d="M12.78 5.22a.75.75 0 0 1 0 1.06L9.06 10l3.72 3.72a.75.75 0 1 1-1.06 1.06l-4.25-4.25a.75.75 0 0 1 0-1.06l4.25-4.25a.75.75 0 0 1 1.06 0Z"
          clipRule="evenodd"
        />
      ) : (
        <path
          fillRule="evenodd"
          d="M7.22 14.78a.75.75 0 0 1 0-1.06L10.94 10 7.22 6.28a.75.75 0 1 1 1.06-1.06l4.25 4.25a.75.75 0 0 1 0 1.06l-4.25 4.25a.75.75 0 0 1-1.06 0Z"
          clipRule="evenodd"
        />
      );

    if (disabled || !url) {
      return (
        <span className={disabledBtn} aria-disabled="true" aria-label={label}>
          <Icon path={path} />
        </span>
      );
    }

    return (
      <button
        type="button"
        onClick={() => onNavigate(url)}
        className={enabledBtn}
        aria-label={label}
      >
        <Icon path={path} />
      </button>
    );
  };

  return (
    <div className="flex flex-col items-center gap-2 py-2">
      <nav className="flex items-center gap-1.5" aria-label="Pages">
        {renderNavButton("Previous", pagination.previousUrl, !pagination.previousUrl, "previous")}
        {pagination.items.map((item, idx) => {
          if (!item) return null;
          if (item.type === "gap") {
            return (
              <span key={`gap-${idx}`} className={gapBtn} aria-hidden="true">
                {item.label}
              </span>
            );
          }

          if (item.current) {
            return (
              <span
                key={`page-${item.label}`}
                className={currentBtn}
                aria-current="page"
              >
                {item.label}
              </span>
            );
          }

          return (
            <button
              key={`page-${item.label}`}
              type="button"
              onClick={() => onNavigate(item.url)}
              className={enabledBtn}
              aria-label={`Page ${item.label}`}
            >
              {item.label}
            </button>
          );
        })}
        {renderNavButton("Next", pagination.nextUrl, !pagination.nextUrl, "next")}
      </nav>
      {pagination.info ? (
        <span className="font-mono text-[11px] text-[#9A9890]">
          {pagination.info}
        </span>
      ) : null}
    </div>
  );
}

function MockupCtaModal({
  hasUploads,
  shopifyCustomerId,
  createUploadUrl,
  onClose,
  onUploadSaved,
}) {
  return (
    <div
      className="absolute inset-x-0 top-0 z-50 px-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="mockup-cta-title"
    >
      <div
        className="fixed inset-0 bg-[#111111]/40 backdrop-blur-[2px]"
        onClick={onClose}
      />
      <div className="relative mx-auto mt-32 w-full max-w-md rounded-lg border border-[#E4E0D6] bg-white shadow-lg overflow-hidden">
        <div className="flex items-start justify-between gap-4 px-6 pt-5 pb-4 border-b border-[#F0EDE4]">
          <div className="flex flex-col gap-1 min-w-0">
            <span className="text-[10px] font-medium uppercase tracking-[0.08em] text-[#9A9890]">
              Mockup studio
            </span>
            <h2
              id="mockup-cta-title"
              className="m-0 text-[15px] font-semibold text-[#111111] leading-tight"
            >
              {hasUploads ? "Pick an upload to mock up" : "Upload a file to start"}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 inline-flex h-7 w-7 items-center justify-center rounded-md text-[#6B6B66] hover:bg-[#F6F4EE] hover:text-[#111111] transition-colors bg-transparent border-0 cursor-pointer"
            aria-label="Close"
          >
            <Icon path={ICONS.xMark} className="h-4 w-4" />
          </button>
        </div>

        <div className="flex flex-col gap-4 px-6 py-5">
          {hasUploads ? (
            <>
              <p className="m-0 text-[13px] text-[#6B6B66] leading-[18px]">
                Choose any file in your library and hit{" "}
                <span className="font-medium text-[#B5651D]">Create mockup</span>{" "}
                to render presentation-ready frames in seconds.
              </p>
              <div className="flex items-center gap-2.5 rounded-md border border-[#E4E0D6] bg-[#FAF8F2] px-3 py-2.5">
                <span className="text-[12px] text-[#111111]">
                  Your uploads are listed below. Pick one to get started.
                </span>
              </div>
              <div className="flex items-center justify-end gap-2 pt-1">
                <button
                  type="button"
                  onClick={onClose}
                  className="inline-flex items-center h-7 rounded-md px-2.5 bg-[#111111] text-white text-[11px] font-medium hover:opacity-90 transition-opacity border-0 cursor-pointer"
                >
                  Got it
                </button>
              </div>
            </>
          ) : (
            <>
              <p className="m-0 text-[13px] text-[#6B6B66] leading-[18px]">
                Mockups need a full-resolution image. Upload a JPG, PNG or TIFF
                and you&apos;ll be able to frame it and render presentation-ready
                mockups in seconds.
              </p>
              <ul className="m-0 pl-0 list-none flex flex-col gap-1.5">
                <li className="flex items-start gap-2 text-[12px] text-[#6B6B66]">
                  <Icon
                    path={ICONS.check}
                    className="h-3.5 w-3.5 text-[#B5651D] shrink-0 mt-0.5"
                  />
                  <span>Original quality gives the best mockup results.</span>
                </li>
                <li className="flex items-start gap-2 text-[12px] text-[#6B6B66]">
                  <Icon
                    path={ICONS.check}
                    className="h-3.5 w-3.5 text-[#B5651D] shrink-0 mt-0.5"
                  />
                  <span>Saved to your library so you can re-render anytime.</span>
                </li>
              </ul>
              <div className="flex items-center justify-end gap-2 pt-1">
                <button
                  type="button"
                  onClick={onClose}
                  className="inline-flex items-center h-7 rounded-md px-2.5 text-[11px] font-medium text-[#6B6B66] hover:text-[#111111] bg-transparent border-0 cursor-pointer"
                >
                  Cancel
                </button>
                <UploaderModal
                  shopify_customer_id={shopifyCustomerId}
                  post_image_url={createUploadUrl}
                  buttonClassName="inline-flex items-center h-7 rounded-md px-2.5 gap-1.5 bg-[#111111] text-white text-[11px] font-medium hover:opacity-90 transition-opacity border-0 cursor-pointer"
                  buttonLabel="Upload a file"
                  onUploadSaved={onUploadSaved}
                />
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function UploadsIndexPage({
  pageData,
  loading,
  error,
  retry,
  onNavigate,
  onDelete,
  onUploadSaved,
}) {
  const [searchValue, setSearchValue] = useState(pageData?.search || "");
  const [showMockupCta, setShowMockupCta] = useState(Boolean(pageData?.mockupCta));

  useEffect(() => {
    setSearchValue(pageData?.search || "");
  }, [pageData?.search]);

  useEffect(() => {
    setShowMockupCta(Boolean(pageData?.mockupCta));
  }, [pageData?.mockupCta]);

  const closeMockupCta = useCallback(() => {
    setShowMockupCta(false);
    if (typeof window !== "undefined") {
      window.history.replaceState(
        {},
        "",
        removeQueryParam(window.location.pathname + window.location.search, "mockup-cta"),
      );
    }
  }, []);

  if (error) return errorCard(error, retry);
  if (!pageData) return loadingCard();

  const hasUploads = pageData.uploads.length > 0;
  const handleSearchSubmit = (event) => {
    event.preventDefault();
    onNavigate(
      buildUploadsIndexUrl(pageData.uploadsUrl, {
        search: searchValue,
        page: null,
      }),
    );
  };

  const clearSearch = () => {
    onNavigate(
      buildUploadsIndexUrl(pageData.uploadsUrl, {
        search: "",
        page: null,
      }),
    );
  };

  return (
    <div className="flex flex-col gap-6 max-w-7xl mx-auto w-full">
      <div className="flex flex-col gap-2">
        <nav
          aria-label="Breadcrumb"
          className="flex items-center gap-1.5 text-[12px] text-[#6B6B66]"
        >
          <a href={pageData.dashboardUrl} className="hover:text-[#111111] no-underline">
            Pro Dashboard
          </a>
          <span className="text-[#9A9890]">/</span>
          <span className="text-[#111111]">Uploads</span>
        </nav>

        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-baseline gap-2 min-w-0">
            <h1 className="text-2xl font-bold tracking-[-0.01em] text-[#111111] truncate m-0">
              File uploads
            </h1>
            <span className="inline-flex items-center h-4 rounded-lg px-1.5 bg-[#F6F4EE] shrink-0">
              <span className="font-mono text-[10px] font-medium text-[#6B6B66] whitespace-nowrap">
                {pageData.uploadsCountLabel}
              </span>
            </span>
            {pageData.search ? (
              <span className="inline-flex items-center h-4 rounded-lg px-1.5 bg-[#FFFBF0] border border-[#EADFBE] shrink-0">
                <span className="text-[10px] font-medium text-[#7A5A0F] whitespace-nowrap">
                  Search: {pageData.search}
                </span>
              </span>
            ) : null}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <form onSubmit={handleSearchSubmit} className="flex items-center">
              <div className="inline-flex items-center h-7 rounded-md pl-2.5 pr-1 gap-1.5 bg-white border border-[#E4E0D6] focus-within:ring-1 focus-within:ring-[#111111]">
                <Icon path={ICONS.magnifyingGlass} className="h-3 w-3 text-[#9A9890]" />
                <input
                  type="text"
                  value={searchValue}
                  onChange={(event) => setSearchValue(event.target.value)}
                  className="border-0 bg-transparent text-[12px] text-[#111111] placeholder-[#9A9890] focus:outline-none focus:ring-0 p-0 w-44"
                  placeholder="Search filenames..."
                />
                {pageData.search ? (
                  <button
                    type="button"
                    onClick={clearSearch}
                    className="text-[10px] font-mono uppercase tracking-[0.06em] text-[#9A9890] hover:text-[#111111] bg-transparent border-0 cursor-pointer px-1.5"
                  >
                    Clear
                  </button>
                ) : (
                  <button
                    type="submit"
                    className="text-[10px] font-mono uppercase tracking-[0.06em] text-[#6B6B66] hover:text-[#111111] bg-transparent border-0 cursor-pointer px-1.5"
                  >
                    Go
                  </button>
                )}
              </div>
            </form>
            <UploaderModal
              shopify_customer_id={pageData.shopifyCustomerId}
              post_image_url={pageData.createUploadUrl}
              buttonClassName="inline-flex items-center h-7 rounded-md px-2.5 gap-1.5 bg-[#111111] text-white text-[11px] font-medium hover:opacity-90 transition-opacity border-0 cursor-pointer"
              buttonLabel="Upload a file"
              onUploadSaved={onUploadSaved}
            />
          </div>
        </div>
      </div>

      <div className="rounded-lg border border-[#E4E0D6] bg-white overflow-hidden">
        {hasUploads ? (
          <div className="flex flex-col px-2 lg:px-3 py-1">
            <div className="hidden md:flex items-center h-9 px-3.5 gap-2 border-b border-[#E4E0D6]">
              <div className="w-12 shrink-0" />
              <div className="grow shrink basis-0 min-w-0 pl-3 text-[10px] font-medium uppercase tracking-[0.08em] text-[#9A9890]">
                Filename
              </div>
              <div className="w-14 shrink-0 text-right text-[10px] font-medium uppercase tracking-[0.08em] text-[#9A9890]">
                W
              </div>
              <div className="w-14 shrink-0 text-right text-[10px] font-medium uppercase tracking-[0.08em] text-[#9A9890]">
                H
              </div>
              <div className="w-16 shrink-0 text-[10px] font-medium uppercase tracking-[0.08em] text-[#9A9890]">
                Format
              </div>
              <div className="w-20 shrink-0 text-right text-[10px] font-medium uppercase tracking-[0.08em] text-[#9A9890] pr-6">
                Size
              </div>
              <div className="w-28 shrink-0 text-[10px] font-medium uppercase tracking-[0.08em] text-[#9A9890] pl-2">
                Uploaded
              </div>
              <div className="w-64 shrink-0" />
            </div>

            {pageData.uploads.map((upload) => (
              <div
                key={upload.key}
                className="flex flex-col md:flex-row md:items-center md:h-[60px] py-2 md:py-0 px-3.5 gap-2 border-b border-[#F0EDE4] last:border-b-0 hover:bg-[#FAF8F2] transition-colors"
              >
                <div className="w-12 shrink-0 flex items-center justify-center">
                  <div className="w-10 h-10 rounded-sm bg-[#F6F4EE] overflow-hidden flex items-center justify-center">
                    <img
                      src={upload.thumbnailUrl}
                      alt=""
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </div>
                </div>

                <div className="grow shrink basis-0 min-w-0 pl-0 md:pl-3">
                  <Link
                    to={upload.showUrl}
                    className="text-[13px] font-medium text-[#111111] hover:text-[#B5651D] no-underline truncate block leading-4"
                  >
                    {upload.filename}
                  </Link>
                </div>

                <div className="w-14 shrink-0 text-right font-mono text-[11px] text-[#111111] hidden md:block">
                  {upload.width}
                </div>
                <div className="w-14 shrink-0 text-right font-mono text-[11px] text-[#111111] hidden md:block">
                  {upload.height}
                </div>

                <div className="w-16 shrink-0 hidden md:block">
                  <span className="inline-flex items-center h-4 rounded-lg px-1.5 bg-[#F6F4EE]">
                    <span className="font-mono text-[10px] font-medium text-[#6B6B66] whitespace-nowrap">
                      {upload.formatLabel || "—"}
                    </span>
                  </span>
                </div>

                <div className="w-20 shrink-0 text-right font-mono text-[11px] text-[#111111] hidden md:block pr-6">
                  {upload.fileSizeLabel || <span className="text-[#9A9890]">—</span>}
                </div>

                <div className="w-28 shrink-0 hidden md:flex flex-col gap-0.5 leading-none pl-2">
                  <span className="text-[12px] text-[#111111]">
                    {upload.createdDateLabel}
                  </span>
                  <span className="font-mono text-[10px] text-[#9A9890] mt-0.5">
                    {upload.createdTimeLabel}
                  </span>
                </div>

                <div className="w-full md:w-64 shrink-0 flex items-center md:justify-end gap-3 whitespace-nowrap">
                  <Link
                    to={upload.previewUrl}
                    className="text-[11px] font-medium text-[#B5651D] hover:underline no-underline"
                  >
                    Create mockup
                  </Link>
                  <span className="text-[#D8D3C5]">·</span>
                  <Link
                    to={upload.showUrl}
                    className="text-[11px] font-medium text-[#6B6B66] hover:text-[#111111] no-underline"
                  >
                    Details
                  </Link>
                  <span className="text-[#D8D3C5]">·</span>
                  <button
                    type="button"
                    onClick={() =>
                      onDelete({
                        deleteUrl: upload.showUrl,
                        page: pageData.pagination?.currentPage,
                        search: pageData.search,
                      })
                    }
                    className="text-[11px] font-medium text-[#7A1F1F] hover:underline bg-transparent border-0 p-0 cursor-pointer"
                    title="Delete upload"
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-16 px-6 gap-2 text-center">
            <span className="text-[10px] font-medium uppercase tracking-[0.08em] text-[#9A9890]">
              Library
            </span>
            <h3 className="m-0 text-[14px] font-semibold text-[#111111]">
              No uploads found
            </h3>
            {pageData.search ? (
              <p className="m-0 text-[12px] text-[#6B6B66]">
                Nothing matched &ldquo;{pageData.search}&rdquo;.
                <button
                  type="button"
                  onClick={clearSearch}
                  className="text-[#B5651D] hover:underline ml-1 bg-transparent border-0 cursor-pointer p-0"
                >
                  Clear search
                </button>
              </p>
            ) : (
              <p className="m-0 text-[12px] text-[#6B6B66]">
                Upload your first file to get started.
              </p>
            )}
          </div>
        )}
      </div>

      {hasUploads ? (
        <TablePagination pagination={pageData.pagination} onNavigate={onNavigate} />
      ) : null}

      {showMockupCta ? (
        <MockupCtaModal
          hasUploads={hasUploads}
          shopifyCustomerId={pageData.shopifyCustomerId}
          createUploadUrl={pageData.createUploadUrl}
          onClose={closeMockupCta}
          onUploadSaved={(responseData) => {
            closeMockupCta();
            onUploadSaved(responseData);
          }}
        />
      ) : null}
    </div>
  );
}

function UploadShowPage({ pageData, loading, error, retry, onDelete }) {
  if (error) return errorCard(error, retry);
  if (!pageData) return loadingCard("Loading upload details…");

  const { upload } = pageData;

  return (
    <div className="flex flex-col gap-6 max-w-7xl mx-auto w-full">
      <div className="flex flex-col gap-2">
        <nav
          aria-label="Breadcrumb"
          className="flex items-center gap-1.5 text-[12px] text-[#6B6B66]"
        >
          <a href={pageData.dashboardUrl} className="hover:text-[#111111] no-underline">
            Pro Dashboard
          </a>
          <span className="text-[#9A9890]">/</span>
          <Link to={pageData.uploadsUrl} className="hover:text-[#111111] no-underline">
            Uploads
          </Link>
          <span className="text-[#9A9890]">/</span>
          <span className="text-[#111111] truncate max-w-[280px]">
            {upload.filename}
          </span>
        </nav>

        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-baseline gap-2 min-w-0">
            <h1
              className="text-2xl font-bold tracking-[-0.01em] text-[#111111] truncate m-0"
              title={upload.filename}
            >
              {upload.basename}
            </h1>
            {upload.aiUpscaled ? (
              <span className="inline-flex items-center h-4 rounded-lg px-1.5 bg-[#111111] shrink-0">
                <span className="text-[9px] font-medium uppercase tracking-[0.05em] text-white whitespace-nowrap">
                  AI Upscaled
                </span>
              </span>
            ) : null}
            {upload.metaPillParts.map((part) => (
              <span
                key={part}
                className="inline-flex items-center h-4 rounded-lg px-1.5 bg-[#F6F4EE] shrink-0"
              >
                <span className="font-mono text-[10px] font-medium text-[#6B6B66] whitespace-nowrap">
                  {part}
                </span>
              </span>
            ))}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <a
              href={pageData.originalFileUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center h-7 rounded-md px-2.5 gap-1.5 border border-[#E4E0D6] bg-white text-[11px] font-medium text-[#111111] hover:bg-[#FAF8F2] no-underline"
            >
              <Icon path={ICONS.arrowTopRight} className="h-3 w-3" />
              Original file
            </a>
            <Link
              to={pageData.previewUrl}
              className="inline-flex items-center h-7 rounded-md px-2.5 gap-1.5 bg-[#111111] text-white text-[11px] font-medium hover:opacity-90 transition-opacity no-underline"
            >
              <Icon path={ICONS.sparkles} className="h-3 w-3" />
              Open mockup studio
            </Link>
          </div>
        </div>
      </div>

      <div className="rounded-lg border border-[#E4E0D6] bg-white overflow-hidden">
        <div className="flex flex-col lg:flex-row">
          <div className="flex-1 min-w-0">
            <div className="border-b border-[#E4E0D6]">
              <div className="flex items-center h-7 px-3.5 mt-2 gap-2">
                <span className="text-[10px] font-medium uppercase tracking-[0.08em] text-[#9A9890]">
                  01 — Featured
                </span>
                <span className="text-[10px] uppercase tracking-[0.06em] text-[#9A9890]">
                  Mockup studio
                </span>
              </div>
              <Link
                to={pageData.previewUrl}
                className="group flex items-stretch py-5 px-6 lg:px-8 gap-6 hover:bg-[#FAF8F2] transition-colors no-underline"
              >
                <div className="hidden sm:flex items-center justify-center w-32 lg:w-40 shrink-0">
                  <div className="relative w-28 h-32">
                    <img
                      src={upload.featuredPreviewUrls.primary}
                      alt=""
                      className="absolute inset-0 w-full h-full object-contain rotate-3 group-hover:rotate-[8deg] transition-transform duration-300 ease-out origin-center"
                    />
                    <img
                      src={upload.featuredPreviewUrls.secondary}
                      alt=""
                      className="absolute inset-0 w-full h-full object-contain -rotate-3 group-hover:-rotate-[8deg] transition-transform duration-300 ease-out origin-center"
                    />
                  </div>
                </div>
                <div className="flex flex-col gap-1.5 flex-1 min-w-0">
                  <span className="text-[10px] font-medium uppercase tracking-[0.08em] text-[#9A9890]">
                    Pro tool
                  </span>
                  <span className="text-[16px] font-semibold tracking-[-0.01em] text-[#111111]">
                    Frame mockup generator
                  </span>
                  <p className="text-[12px] text-[#6B6B66] leading-[16px] m-0 max-w-md">
                    Generate presentation-ready framed mockups for your website
                    and marketing materials.
                  </p>
                  <span className="mt-1 text-[11px] font-medium text-[#B5651D] group-hover:underline">
                    Open mockup generator →
                  </span>
                </div>
              </Link>
            </div>

            <div className="border-b border-[#E4E0D6]">
              <div className="flex items-center h-7 px-3.5 mt-2 gap-2">
                <span className="text-[10px] font-medium uppercase tracking-[0.08em] text-[#9A9890]">
                  02 — Create a product
                </span>
                <span className="text-[10px] uppercase tracking-[0.06em] text-[#9A9890]">
                  From this upload
                </span>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 border-t border-[#F0EDE4]">
                {PRODUCT_CARDS.map((product, idx) => (
                  <a
                    key={product.id}
                    href={pageData.productLinks[product.id]}
                    className={`group flex flex-col gap-2 py-5 px-6 lg:px-8 hover:bg-[#FAF8F2] transition-colors no-underline ${
                      idx < 2 ? "sm:border-r border-[#F0EDE4]" : ""
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <span className="inline-flex h-6 w-6 items-center justify-center rounded-sm bg-[#F6F4EE] text-[#6B6B66]">
                        <Icon path={product.icon} className="h-3.5 w-3.5" />
                      </span>
                      <span className="text-[13px] font-semibold text-[#111111]">
                        {product.title}
                      </span>
                    </div>
                    <p className="text-[11px] text-[#6B6B66] leading-[14px] m-0">
                      {product.body}
                    </p>
                    <span className="mt-1 text-[11px] font-medium text-[#B5651D] group-hover:underline">
                      {product.cta} →
                    </span>
                  </a>
                ))}
              </div>
            </div>

            <div className="border-b border-[#E4E0D6]">
              <div className="flex items-center h-7 px-3.5 mt-2 gap-2">
                <span className="text-[10px] font-medium uppercase tracking-[0.08em] text-[#9A9890]">
                  03 — File details
                </span>
              </div>

              <div className="flex flex-col py-2 px-6 lg:px-8">
                {[
                  ["Resolution", upload.resolution, upload.megapixels],
                  ["Format", upload.format || "—"],
                  ["File size", upload.fileSize || "Unknown"],
                  ["Filename", upload.filename],
                  ["Image key", upload.key],
                  ["Uploaded", upload.uploadedAt, `${upload.uploadedAgo} ago`],
                ].map(([label, value, detail], idx) => (
                  <div
                    key={label}
                    className={`flex items-baseline justify-between py-2.5 gap-3 ${
                      idx < 5 ? "border-b border-[#F0EDE4]" : ""
                    }`}
                  >
                    <span className="text-[11px] font-medium uppercase tracking-[0.06em] text-[#9A9890]">
                      {label}
                    </span>
                    <span className="text-[12px] text-[#111111] truncate min-w-0 text-right flex items-center gap-2">
                      <span className={label === "Filename" || label === "Image key" ? "font-mono" : ""}>
                        {value}
                      </span>
                      {detail ? (
                        <span className="font-mono text-[10px] text-[#9A9890]">
                          {detail}
                        </span>
                      ) : null}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <div className="flex items-center h-7 px-3.5 mt-2 gap-2">
                <span className="text-[10px] font-medium uppercase tracking-[0.08em] text-[#9A9890]">
                  04 — Manage
                </span>
              </div>

              <div className="flex items-center gap-3 py-3 px-6 lg:px-8">
                <a
                  href={pageData.originalFileUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[11px] font-medium text-[#B5651D] hover:underline no-underline"
                >
                  View original file
                </a>
                <span className="text-[#D8D3C5]">·</span>
                <button
                  type="button"
                  onClick={() => onDelete({ deleteUrl: pageData.deleteUrl })}
                  className="text-[11px] font-medium text-[#7A1F1F] hover:underline bg-transparent border-0 p-0 cursor-pointer"
                >
                  Delete this upload
                </button>
              </div>
            </div>
          </div>

          <div className="lg:w-[320px] shrink-0 border-t lg:border-t-0 lg:border-l border-[#E4E0D6]">
            <div className="flex flex-col py-5 px-6 gap-2.5 border-b border-[#E4E0D6]">
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-medium uppercase tracking-[0.08em] text-[#9A9890]">
                  Source artwork
                </span>
                <a
                  href={pageData.originalFileUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[11px] font-medium text-[#B5651D] hover:underline no-underline"
                >
                  View original
                </a>
              </div>
              <div className="rounded-md overflow-hidden bg-[#F6F4EE] border border-[#E4E0D6]">
                <img
                  src={upload.previewImageUrl}
                  alt={upload.filename}
                  className="w-full h-auto block"
                />
              </div>
              <div className="flex items-center justify-between gap-2 pt-1">
                <span
                  className="font-mono text-[10px] text-[#9A9890] truncate min-w-0"
                  title={upload.filename}
                >
                  {upload.filename}
                </span>
                {upload.resolution ? (
                  <span className="font-mono text-[10px] text-[#6B6B66] shrink-0">
                    {upload.resolution}
                  </span>
                ) : null}
              </div>
            </div>

            <div className="flex flex-col py-5 px-6 gap-2.5 border-b border-[#E4E0D6]">
              <span className="text-[10px] font-medium uppercase tracking-[0.08em] text-[#9A9890]">
                Quick actions
              </span>
              <div className="flex flex-col gap-1.5">
                <Link
                  to={pageData.previewUrl}
                  className="text-[12px] text-[#111111] hover:text-[#B5651D] no-underline"
                >
                  Open mockup studio
                </Link>
                <a
                  href={pageData.productLinks.paper}
                  className="text-[12px] text-[#111111] hover:text-[#B5651D] no-underline"
                >
                  Print &amp; frame
                </a>
                <a
                  href={pageData.productLinks.canvas}
                  className="text-[12px] text-[#111111] hover:text-[#B5651D] no-underline"
                >
                  Framed canvas
                </a>
                <a
                  href={pageData.productLinks.printOnly}
                  className="text-[12px] text-[#111111] hover:text-[#B5651D] no-underline"
                >
                  Unframed print
                </a>
              </div>
            </div>

            <div className="flex flex-col py-5 px-6 gap-2.5">
              <span className="text-[10px] font-medium uppercase tracking-[0.08em] text-[#9A9890]">
                Tips
              </span>
              <p className="text-[11px] text-[#6B6B66] leading-[14px] m-0">
                For best results, upload a high-resolution JPG with embedded
                colour profile. Files at 300 DPI for the final print size
                produce sharpest output.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function PreviewRoutePage({ pageData, loading, error, retry, onNavigate }) {
  if (error) return errorCard(error, retry);
  if (!pageData) return loadingCard("Loading mockup studio…");

  return (
    <MockupStudio
      {...pageData}
      internalNavigate={onNavigate}
    />
  );
}

function ProUploadsRouter({ initialRouteType, initialData, currentUrl }) {
  const location = useLocation();
  const navigate = useNavigate();
  const normalizedInitialUrl = useMemo(
    () => normalizeRelativeUrl(currentUrl),
    [currentUrl],
  );
  const [refreshNonce, setRefreshNonce] = useState(0);
  const routeType = getUploadsRouteType(location.pathname);
  const locationUrl = normalizeRelativeUrl(
    `${location.pathname}${location.search}${location.hash}`,
  );
  const cacheRef = useRef(
    new Map([[normalizedInitialUrl, { routeType: initialRouteType, data: initialData }]]),
  );
  const [pageState, setPageState] = useState({
    routeType: initialRouteType,
    data: initialData,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const invalidateUploadsCache = useCallback(() => {
    Array.from(cacheRef.current.keys()).forEach((key) => {
      if (key.startsWith("/apps/frame/pro/uploads")) {
        cacheRef.current.delete(key);
      }
    });
  }, []);

  const retryCurrent = useCallback(() => {
    cacheRef.current.delete(locationUrl);
    setRefreshNonce((value) => value + 1);
  }, [locationUrl]);

  const navigateWithin = useCallback(
    (url) => {
      navigate(normalizeRelativeUrl(url));
    },
    [navigate],
  );

  const refreshToUrl = useCallback(
    (url) => {
      const normalizedUrl = normalizeRelativeUrl(url || locationUrl);
      cacheRef.current.delete(normalizedUrl);

      if (normalizedUrl === locationUrl) {
        setRefreshNonce((value) => value + 1);
      } else {
        navigate(normalizedUrl);
      }
    },
    [locationUrl, navigate],
  );

  const handleUploadSaved = useCallback(
    (responseData) => {
      const nextUrl =
        responseData?.url || responseData?.upload?.showUrl || locationUrl;
      invalidateUploadsCache();
      refreshToUrl(nextUrl);
    },
    [invalidateUploadsCache, locationUrl, refreshToUrl],
  );

  const handleDelete = useCallback(
    async ({ deleteUrl, page, search }) => {
      if (!window.confirm("Are you sure you want to delete this upload?")) {
        return;
      }

      try {
        const requestUrl = new URL(deleteUrl, window.location.origin);
        const formData = new URLSearchParams();
        formData.set("_method", "delete");
        if (page) formData.set("page", page);
        if (search) formData.set("search", search);

        const response = await fetch(normalizeRelativeUrl(requestUrl.toString()), {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/x-www-form-urlencoded; charset=UTF-8",
          },
          credentials: "same-origin",
          body: formData.toString(),
        });

        if (response.redirected) {
          window.location.assign(response.url);
          return;
        }

        if (!response.ok) {
          throw new Error(`Delete failed with HTTP ${response.status}`);
        }

        const json = await response.json();
        invalidateUploadsCache();
        refreshToUrl(json.redirectUrl || buildUploadsIndexUrl("/apps/frame/pro/uploads"));
      } catch (deleteError) {
        // eslint-disable-next-line no-console
        console.error("Failed to delete upload:", deleteError);
        // eslint-disable-next-line no-alert
        window.alert("Couldn't delete this upload. Please try again.");
      }
    },
    [invalidateUploadsCache, refreshToUrl],
  );

  useEffect(() => {
    const cached = cacheRef.current.get(locationUrl);
    if (cached) {
      setPageState(cached);
      setLoading(false);
      setError(null);
      return;
    }

    if (!routeType) {
      setError("Unknown uploads route.");
      setLoading(false);
      return;
    }

    const abortController = new AbortController();
    let active = true;

    setLoading(true);
    setError(null);

    fetch(locationUrl, {
      headers: { Accept: "application/json" },
      credentials: "same-origin",
      signal: abortController.signal,
    })
      .then(async (response) => {
        if (response.redirected) {
          window.location.assign(response.url);
          return null;
        }

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        return response.json();
      })
      .then((json) => {
        if (!active || !json) return;

        const nextPageState = { routeType, data: json };
        cacheRef.current.set(locationUrl, nextPageState);
        setPageState(nextPageState);
      })
      .catch((fetchError) => {
        if (!active || fetchError?.name === "AbortError") return;
        // eslint-disable-next-line no-console
        console.error("Failed to load Pro uploads page:", fetchError);
        setError(
          fetchError?.message ||
            "Something went wrong while loading this uploads page.",
        );
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
      abortController.abort();
    };
  }, [locationUrl, refreshNonce, routeType]);

  const routeData = useCallback(
    (expectedRouteType) =>
      pageState.routeType === expectedRouteType && routeType === expectedRouteType
        ? pageState.data
        : null,
    [pageState, routeType],
  );

  return (
    <Routes>
      <Route
        path="/apps/frame/pro/uploads"
        element={
          <UploadsIndexPage
            pageData={routeData("index")}
            loading={loading}
            error={routeType === "index" ? error : null}
            retry={retryCurrent}
            onNavigate={navigateWithin}
            onDelete={(payload) =>
              handleDelete(payload).catch((deleteError) => {
                // eslint-disable-next-line no-console
                console.error(deleteError);
                window.alert("We couldn't delete that upload. Please try again.");
              })
            }
            onUploadSaved={handleUploadSaved}
          />
        }
      />
      <Route
        path="/apps/frame/pro/uploads/:id/preview"
        element={
          <PreviewRoutePage
            pageData={routeData("preview")}
            loading={loading}
            error={routeType === "preview" ? error : null}
            retry={retryCurrent}
            onNavigate={navigateWithin}
          />
        }
      />
      <Route
        path="/apps/frame/pro/uploads/:id"
        element={
          <UploadShowPage
            pageData={routeData("show")}
            loading={loading}
            error={routeType === "show" ? error : null}
            retry={retryCurrent}
            onDelete={(payload) =>
              handleDelete(payload).catch((deleteError) => {
                // eslint-disable-next-line no-console
                console.error(deleteError);
                window.alert("We couldn't delete that upload. Please try again.");
              })
            }
          />
        }
      />
    </Routes>
  );
}

export default function ProUploadsApp(props) {
  return (
    <BrowserRouter>
      <ProUploadsRouter {...props} />
    </BrowserRouter>
  );
}
